import React from 'react';
import { useTranslation } from 'react-i18next';
import { CallActivityLogEntry } from '../../services/api/call-log.types';
import { EventDetailEntry, getEventDetailEntries } from './call-activity-event-details.utils';
import { formatTraceTimestamp } from './call-activity-log.utils';
import styles from './CallActivityLogPanel.module.css';

function EventDetailContent({ entries }: { entries: EventDetailEntry[] }) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <dl className={styles.timelineDetails}>
      {entries.map((entry, index) =>
        'items' in entry ? (
          <div key={`${entry.label}-${index}`} className={styles.timelineDetailGroup}>
            <dt>{entry.label}</dt>
            <dd>
              <ul className={styles.timelineDetailList}>
                {entry.items.map((item, itemIndex) => (
                  <li key={`${item}-${itemIndex}`}>{item}</li>
                ))}
              </ul>
            </dd>
          </div>
        ) : (
          <div key={`${entry.label}-${index}`} className={styles.timelineDetailRow}>
            <dt>{entry.label}</dt>
            <dd>{entry.value}</dd>
          </div>
        )
      )}
    </dl>
  );
}

interface CallActivityEventTimelineProps {
  events: CallActivityLogEntry[];
  className?: string;
}

export function CallActivityEventTimeline({ events, className }: CallActivityEventTimelineProps) {
  const { t } = useTranslation('activityLog');

  if (events.length === 0) {
    return null;
  }

  return (
    <ol
      className={className ? `${styles.timeline} ${className}` : styles.timeline}
      aria-label={t('timelineLabel')}
    >
      {events.map((event, index) => {
        const detailEntries = getEventDetailEntries(event, t);
        const isLast = index === events.length - 1;

        return (
          <li
            key={event.logId}
            className={`${styles.timelineItem} ${isLast ? styles.timelineItemLast : ''}`}
          >
            <span className={styles.timelineMarker} aria-hidden="true" />
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <span className={styles.traceEventType}>
                  {t(`events.${event.eventType}`, event.eventType)}
                </span>
                <span className={styles.traceEventTime}>
                  {formatTraceTimestamp(event.createdAt)}
                </span>
              </div>
              {event.cxoneContactId && (
                <div className={styles.timelineMeta}>
                  {t('details.cxoneContactId')}: {event.cxoneContactId}
                </div>
              )}
              <EventDetailContent entries={detailEntries} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default CallActivityEventTimeline;
